import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getLocale, useIntl } from '@edx/frontend-platform/i18n';
import { getAuthenticatedUser } from '@edx/frontend-platform/auth';

import { cohortsApi } from '../../api';
import { useHeaderFooter, useCohortInfo } from '../../hooks';
import { getLanguageDisplayName, resolveApiErrorMessage } from '../../utils';
import { initialsOf } from '../CohortDetail/utils';

import messages from './messages';

import '../cohortSubpage.scss';
import './index.scss';

interface JoinCohortProps {
  children?: React.ReactNode;
}

/**
 * Sets the post-auth redirect target and sends the visitor to log in/register,
 * so the app lands back on this invite page after SSO.
 */
const redirectToAuth = (path: '/login' | '/signup'): void => {
  localStorage.setItem('redirect-path', window.location.pathname);
  window.location.href = `${path}?redirect-to=${window.location.href}`;
};

/** Wraps the emphasized `<b>` chunk of the forum blurb; module-scoped so it is
 * not re-created on every render (react/no-unstable-nested-components). */
const renderBold = (chunks: React.ReactNode): React.ReactNode => <strong>{chunks}</strong>;

/** Two-people glyph shown in the invite card header (matches the Figma frame). */
const InviteIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <circle cx="9" cy="8.5" r="3.2" />
    <path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" strokeLinecap="round" />
    <circle cx="16.5" cy="8" r="2.4" opacity="0.55" />
    <path d="M15 13.4c2.8-.4 5.5 1.5 5.5 4.6" strokeLinecap="round" opacity="0.55" />
  </svg>
);

const JoinCohort: React.FC<JoinCohortProps> = ({ children }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { headerContent, footerContent } = useHeaderFooter(children);
  const { info, loaded, errorStatus } = useCohortInfo(slug);

  const [isJoining, setIsJoining] = useState<boolean>(false);
  const [joinErrorMessage, setJoinErrorMessage] = useState<string | null>(null);

  const authenticatedUser = getAuthenticatedUser();

  // Naming only: eligibility is never computed here, `viewer_can_join` is the server's answer.
  const cohortLanguageName = info ? getLanguageDisplayName(info.language, getLocale()) : '';
  const isJoinDisabled = info?.viewer_can_join === false;

  // Join is idempotent and returns `already_member`, so a single action covers
  // both the not-yet-member and already-a-member cases.
  const handleJoin = async (): Promise<void> => {
    if (!slug) {
      return;
    }
    setIsJoining(true);
    setJoinErrorMessage(null);

    await cohortsApi.joinCohort(
      slug,
      () => navigate(`/cohorts/${slug}`),
      (error) => {
        // Backstop for a join the card offered but the server refused; else the server `detail`.
        setJoinErrorMessage(resolveApiErrorMessage(
          error,
          {
            language_mismatch: intl.formatMessage(
              messages.languageMismatchMessage,
              { language: cohortLanguageName },
            ),
          },
          intl.formatMessage(messages.joinError),
        ));
        setIsJoining(false);
      },
    );
  };

  return (
    <>
      <input type="hidden" id="anPageName" name="page" defaultValue="join-cohort" />
      <div className="container-center-horizontal">
        <div className="join-cohort screen cohort-subpage">
          <div className="homepage">
            {headerContent}
          </div>
          <div className="legatum-container">
            <div className="c-section join-cohort-center">
              {loaded && !info && (
                <div className="join-cohort-card join-cohort-card--message">
                  <p className="leg-p">
                    {intl.formatMessage(errorStatus === 404
                      ? messages.notFoundMessage
                      : messages.loadErrorMessage)}
                  </p>
                </div>
              )}
              {loaded && info && (
                <div className="join-cohort-card">
                  <div className="join-cohort-icon">
                    <InviteIcon />
                  </div>
                  <p className="join-cohort-eyebrow">{intl.formatMessage(messages.invitedToJoin)}</p>
                  <h1 className="join-cohort-title">{info.name}</h1>
                  {info.description && (
                    <p className="join-cohort-description">{info.description}</p>
                  )}
                  <hr className="join-cohort-divider" />

                  <div className="join-cohort-details">
                    <div className="join-cohort-detail-label">{intl.formatMessage(messages.courseLabel)}</div>
                    <div className="join-cohort-detail-value">{intl.formatMessage(messages.courseName)}</div>
                    <div className="join-cohort-detail-label join-cohort-detail-label--spaced">
                      {intl.formatMessage(messages.membersLabel)}
                    </div>
                    <div className="join-cohort-detail-value">
                      {intl.formatMessage(messages.memberCountLabel, { count: info.member_count })}
                    </div>
                    <div className="join-cohort-detail-label join-cohort-detail-label--spaced">
                      {intl.formatMessage(messages.facilitatorLabel)}
                    </div>
                    <div className="join-cohort-facilitator">
                      <span className="join-cohort-avatar" aria-hidden="true">{initialsOf(info.owner_name)}</span>
                      <span className="join-cohort-facilitator-name">{info.owner_name}</span>
                    </div>
                    <div className="join-cohort-detail-label join-cohort-detail-label--spaced">
                      {intl.formatMessage(messages.languageLabel)}
                    </div>
                    <div className="join-cohort-detail-value">{cohortLanguageName}</div>
                  </div>

                  <p className="join-cohort-blurb">
                    {intl.formatMessage(messages.forumBlurb, { b: renderBold })}
                  </p>

                  {!authenticatedUser ? (
                    <>
                      <div className="join-cohort-actions">
                        <button
                          type="button"
                          className="join-cohort-btn join-cohort-btn--primary"
                          onClick={() => redirectToAuth('/login')}
                        >
                          {intl.formatMessage(messages.loginButton)}
                        </button>
                        <button
                          type="button"
                          className="join-cohort-btn join-cohort-btn--secondary"
                          onClick={() => redirectToAuth('/signup')}
                        >
                          {intl.formatMessage(messages.registerButton)}
                        </button>
                      </div>
                      <p className="join-cohort-anon">{intl.formatMessage(messages.anonymousMessage)}</p>
                    </>
                  ) : (
                    <>
                      <div className="join-cohort-actions">
                        <button
                          type="button"
                          className="join-cohort-btn join-cohort-btn--primary"
                          onClick={handleJoin}
                          disabled={isJoining || isJoinDisabled}
                          aria-describedby={isJoinDisabled ? 'join-cohort-language-mismatch' : undefined}
                        >
                          {intl.formatMessage(isJoining ? messages.joiningButton : messages.joinButton)}
                        </button>
                        <button
                          type="button"
                          className="join-cohort-btn join-cohort-btn--secondary"
                          onClick={() => navigate('/')}
                        >
                          {intl.formatMessage(messages.goBackButton)}
                        </button>
                      </div>
                      {isJoinDisabled && (
                        <p className="join-cohort-language-mismatch" id="join-cohort-language-mismatch">
                          {intl.formatMessage(messages.languageMismatchMessage, { language: cohortLanguageName })}
                        </p>
                      )}
                    </>
                  )}
                  {joinErrorMessage && <p className="form-error form-error-custom">{joinErrorMessage}</p>}
                </div>
              )}
            </div>
          </div>
          {footerContent}
        </div>
      </div>
    </>
  );
};

export default JoinCohort;
